import React, { useEffect, useRef, useCallback } from 'react';
import { useAppSelector, useAppDispatch } from '@/store/hooks';
import { emojiActions } from '@/store/slices/emojiSlice';
import LottieEmoji from './LottieEmoji';
import { formatUserName } from '@/utils/formatUtils';

const EMOJI_COLORS = ["#499ae7", "#2e9f85", "#c687e9"];
const EMOJI_LIFETIME_MS = 3000;

interface EmojiProps {
  userId: string;
}

const Emoji: React.FC = () => {
  const dispatch = useAppDispatch();
  const emojiUserList = useAppSelector((s) => s.emoji.emojiUserList);
  const reactionLayerBottom = useAppSelector((s) => s.emoji.reactionLayerBottom);
  const timersRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());

  // 전역 모든 리액션 표시 (필터링 제거)
  const userEmojis = emojiUserList;

  // ─── 자동 제거 타이머 ─────────────────────────────────────────────────
  useEffect(() => {
    userEmojis.forEach((item) => {
      if (!timersRef.current.has(item.id)) {
        const timer = setTimeout(() => {
          dispatch(emojiActions.removeEmoji(item.id));
          timersRef.current.delete(item.id);
        }, EMOJI_LIFETIME_MS);
        timersRef.current.set(item.id, timer);
      }
    });

    // 클린업: 리스트에서 이미 사라진 항목의 타이머 정리
    timersRef.current.forEach((timer, id) => {
      if (!userEmojis.find((e) => e.id === id)) {
        clearTimeout(timer);
        timersRef.current.delete(id);
      }
    });
  }, [userEmojis, dispatch]);

  useEffect(() => {
    return () => {
      timersRef.current.forEach((timer) => clearTimeout(timer));
      timersRef.current.clear();
    };
  }, []);

  const getColor = useCallback((userId: string) => {
    const idString = String(userId);
    const charSum = idString.split("").reduce((acc, char) => acc + char.charCodeAt(0), 0);
    return EMOJI_COLORS[charSum % EMOJI_COLORS.length];
  }, []);

  const getPosition = useCallback((timestamp: number) => {
    // 타임스탬프 기반 랜덤 위치 (20%~80% 사이)
    return (timestamp % 61) + 20;
  }, []);

  if (userEmojis.length === 0) return null;

  const bottomValue = reactionLayerBottom > 0 ? reactionLayerBottom : 100;

  return (
    <div
      className="rtc_profileEmoji_newWrap"
      style={{
        position: "absolute",
        zIndex: 1300,
        pointerEvents: "none",
        overflow: "visible",
        bottom: bottomValue,
        transition: 'bottom 0.2s ease',
      }}
    >
      {userEmojis.map((item) => {
        const isLottieSupported = [
          'THUMBS_UP', 'CLAP_HAND', 'HUNDRED_POINT', 'OK',
          'PARTY_POPPER', 'SMILE_WITH_HEART_EYES', 'FIRE', 'TWO_HEARTS'
        ].includes(item.emoji);

        const leftPos = getPosition(item.timestamp);

        if (isLottieSupported) {
          return (
            <LottieEmoji
              key={item.id}
              senderName={formatUserName(item.userName)}
              type={item.emoji}
              color={getColor(item.userId)}
              left={leftPos}
            />
          );
        }

        // 캐릭터 이모티콘 등 Lottie 미지원 항목에 대한 Fallback
        return (
          <div
            key={item.id}
            className="rtc_profileEmoji_new rtc_profileEmoji_active"
            style={{
              position: "absolute",
              left: `${leftPos}%`,
              zIndex: 1203,
            }}
          >
             <div className="rtc_profileEmoji_newLottie">
                <span className={`charemo_inner ${item.emoji}`} style={{ display: 'block', width: 56, height: 56 }} />
             </div>
             <span className="rtc_profileEmoji_sender" style={{ background: getColor(item.userId) }}>
               <span className="rtc_profileEmoji_senderText">{formatUserName(item.userName)}</span>
             </span>
          </div>
        );
      })}
    </div>
  );
};

export default Emoji;
